import React from "react";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";
import { getCountryShort } from "@/shared/utils/formatters";

interface MarketTooltipProps {
	name: string;
	statusPhase: string;
	children: React.ReactNode;
}

/**
 * Maps CampaignStatusPhase to its corresponding UI badge colors based on the design image.
 */
const getStatusBadgeStyle = (phase: string): React.CSSProperties => {
	const base: React.CSSProperties = {
		padding: "2px 8px",
		borderRadius: "4px",
		fontSize: "12px",
		fontWeight: 400,
		marginLeft: "8px",
		display: "inline-flex",
		alignItems: "center",
		background: "#454545",
	};

	const p = phase.toLowerCase();

	// Color mapping based on the provided image
	if (p.includes("video production") || p.includes("scripting")) {
		return { ...base, color: "#3A0CA3", border: "1px solid #3A0CA3" }; // Blue/Purple
	}
	if (p.includes("shortlisting") || p.includes("briefing") || p.includes("proposal")) {
		return { ...base, color: "#0096C7", border: "1px solid #0096C7" }; // Cyan
	}
	if (p.includes("go live")) {
		return { ...base, color: "#F72586", border: "1px solid #F72586" }; // Pink
	}
	if (p.includes("complete")) {
		return { ...base, color: "#4CAF50", border: "1px solid #4CAF50" }; // Green
	}

	// Default fallback (similar to "Parked" or "Cancelled" in image)
	return { ...base, color: "#666", border: "1px solid #E5E5E5", background: "#F5F5F5" };
};

export const MarketTooltip: React.FC<MarketTooltipProps> = ({ name, statusPhase, children }) => {
	const shortCode = getCountryShort(name);

	const title = (
		<div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 2px" }}>
			<span style={{ fontSize: "14px", fontWeight: 400, color: "#FFFFFF" }}>
				{name} {shortCode ? `(${shortCode})` : ""}
			</span>
			<span style={getStatusBadgeStyle(statusPhase)}>{statusPhase}</span>
		</div>
	);

	return (
		<AppTooltip
			title={title}
			placement="bottomLeft"
			backgroundColor="#333333"
			blurPx={6}
			mouseEnterDelay={0.2}
			arrow={false}
		>
			<div style={{ display: "inline-flex" }}>{children}</div>
		</AppTooltip>
	);
};
